import { useCallback, useEffect, useRef, useState } from 'react';
import {
  entitlementUploadService,
  EntitlementUploadJobProgress,
  EntitlementUploadJobStatus,
} from '../services/entitlementUploadService';
import { validateEntitlementUploadFile } from '../utils/entitlementUploadFile';

const POLLING_INTERVAL_MS = 2_000;
const terminalStatuses: EntitlementUploadJobStatus[] = ['completed', 'failed', 'expired'];

export interface EntitlementUploadState {
  status: 'idle' | 'validating' | 'uploading' | EntitlementUploadJobStatus;
  uploadProgress: number;
  totalRows: number | null;
  processedRows: number;
  deliveredRows: number;
  pendingRows: number;
  errorRows: number;
  createdEntitlements: number;
  createdPendingEntitlements: number;
  report?: EntitlementUploadJobProgress['report'];
  failure?: string;
}

const initialState: EntitlementUploadState = {
  status: 'idle',
  uploadProgress: 0,
  totalRows: null,
  processedRows: 0,
  deliveredRows: 0,
  pendingRows: 0,
  errorRows: 0,
  createdEntitlements: 0,
  createdPendingEntitlements: 0,
};

const toJobState = (progress: EntitlementUploadJobProgress): EntitlementUploadState => ({
  status: progress.status,
  uploadProgress: 100,
  totalRows: progress.totalRows,
  processedRows: progress.processedRows,
  deliveredRows: progress.deliveredRows,
  pendingRows: progress.pendingRows,
  errorRows: progress.errorRows,
  createdEntitlements: progress.createdEntitlements,
  createdPendingEntitlements: progress.createdPendingEntitlements,
  report: progress.report,
  failure: progress.failure?.message ?? (progress.status === 'failed'
    ? 'O processamento do upload não pôde ser concluído.'
    : undefined),
});

export function useEntitlementUpload() {
  const [state, setState] = useState<EntitlementUploadState>(initialState);
  const pollingTimer = useRef<ReturnType<typeof setTimeout>>();
  const mounted = useRef(true);
  const pollingSession = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollingTimer.current) clearTimeout(pollingTimer.current);
    pollingTimer.current = undefined;
  }, []);

  const poll = useCallback(async (jobId: string, session: number): Promise<void> => {
    try {
      const progress = await entitlementUploadService.getStatus(jobId);
      if (!mounted.current || pollingSession.current !== session) return;

      setState(toJobState(progress));
      if (!terminalStatuses.includes(progress.status)) {
        pollingTimer.current = setTimeout(() => void poll(jobId, session), POLLING_INTERVAL_MS);
      }
    } catch {
      if (mounted.current && pollingSession.current === session) {
        setState((current) => ({
          ...current,
          failure: 'Não foi possível atualizar o andamento do processamento.',
        }));
        pollingTimer.current = setTimeout(() => void poll(jobId, session), POLLING_INTERVAL_MS);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pollingSession.current += 1;
      stopPolling();
    };
  }, [stopPolling]);

  const start = useCallback(async (file: File) => {
    stopPolling();
    const session = pollingSession.current + 1;
    pollingSession.current = session;
    setState({ ...initialState, status: 'validating' });

    try {
      const totalRows = await validateEntitlementUploadFile(file);
      if (!mounted.current) return;

      setState({ ...initialState, status: 'uploading', totalRows });
      const job = await entitlementUploadService.createJob(file, (uploadProgress) => {
        if (mounted.current && pollingSession.current === session) {
          setState((current) => ({ ...current, uploadProgress }));
        }
      });
      if (!mounted.current || pollingSession.current !== session) return;

      setState((current) => ({ ...current, status: job.status, uploadProgress: 100 }));
      await poll(job.jobId, session);
    } catch {
      if (mounted.current && pollingSession.current === session) {
        setState({
          ...initialState,
          status: 'failed',
          failure: 'Não foi possível iniciar o processamento do arquivo.',
        });
      }
    }
  }, [poll, stopPolling]);

  const downloadReport = useCallback(async () => {
    if (!state.report) return;
    try {
      await entitlementUploadService.downloadReport(state.report);
    } catch {
      if (mounted.current) {
        setState((current) => ({
          ...current,
          failure: 'Não foi possível baixar o relatório.',
        }));
      }
    }
  }, [state.report]);

  const clear = useCallback(() => {
    stopPolling();
    pollingSession.current += 1;
    setState(initialState);
  }, [stopPolling]);

  return { state, start, downloadReport, clear };
}
